import { useMemo } from 'react';
import { Alert, Button, Empty, Input, Popconfirm, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import type { EnergyPack, EnergyPackStatus, PageMeta } from '@goelectric/shared-types';
import type { PackFilters } from '../hooks/usePacks';
import {
  formatCop,
  formatKwh,
  formatVigencia,
  PACK_STATUS_LABELS,
  PACK_STATUS_VALUES,
} from '../utils/format';
import { PackStatusBadge } from './PackBadges';

const STATUS_OPTIONS: { value: EnergyPackStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Todos los estados' },
  ...PACK_STATUS_VALUES.map((value) => ({ value, label: PACK_STATUS_LABELS[value] })),
];

export interface PackTableProps {
  packs: EnergyPack[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  filters: PackFilters;
  /** Muestra las acciones de escritura (editar/eliminar) y el CTA del empty-state. */
  canWrite: boolean;
  /** `false` para ALIADO_ADMIN/CONTADOR: el backend solo permite `ACTIVE`. */
  canFilterStatus: boolean;
  onFiltersChange: (filters: PackFilters) => void;
  onOffsetChange: (offset: number) => void;
  onRetry: () => void;
  onCreate: () => void;
  onSelect: (pack: EnergyPack) => void;
  onEdit: (pack: EnergyPack) => void;
  onDelete: (pack: EnergyPack) => void;
}

/** `true` si hay búsqueda o un estado distinto del default del rol. */
function hasActiveFilters(filters: PackFilters, canFilterStatus: boolean): boolean {
  return (
    Boolean(filters.q?.trim()) ||
    (canFilterStatus && filters.status !== undefined && filters.status !== 'ALL')
  );
}

/**
 * Tabla CRUD de Packs de Energía (guía de estilo §8): búsqueda por nombre con
 * debounce (en `usePacks`), filtro por estado, paginación server-side, empty-state
 * con acción sugerida y badge de estado.
 */
export function PackTable({
  packs,
  meta,
  loading,
  error,
  filters,
  canWrite,
  canFilterStatus,
  onFiltersChange,
  onOffsetChange,
  onRetry,
  onCreate,
  onSelect,
  onEdit,
  onDelete,
}: PackTableProps) {
  const filtered = hasActiveFilters(filters, canFilterStatus);

  const columns = useMemo<ColumnsType<EnergyPack>>(
    () => [
      {
        title: 'Nombre',
        key: 'name',
        fixed: 'left',
        width: 260,
        render: (_, pack) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{pack.name}</Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}
            >
              {pack.id}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'kWh',
        dataIndex: 'kwh',
        key: 'kwh',
        width: 130,
        align: 'right',
        render: (value: number) => formatKwh(value),
      },
      {
        title: 'Precio',
        key: 'priceCop',
        width: 160,
        align: 'right',
        render: (_, pack) => formatCop(pack.priceCop, pack.currency || 'COP'),
      },
      {
        title: 'Vigencia',
        key: 'vigencia',
        width: 250,
        render: (_, pack) => formatVigencia(pack.validFrom, pack.validTo),
      },
      {
        title: 'Estado',
        key: 'status',
        width: 170,
        render: (_, pack) => <PackStatusBadge status={pack.status} validTo={pack.validTo} />,
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: canWrite ? 240 : 90,
        align: 'right',
        render: (_, pack) => (
          <Space size={4}>
            <Button
              size="small"
              aria-label={`Ver ${pack.name}`}
              onClick={() => onSelect(pack)}
            >
              Ver
            </Button>
            {canWrite && (
              <>
                <Button
                  size="small"
                  aria-label={`Editar ${pack.name}`}
                  onClick={() => onEdit(pack)}
                >
                  Editar
                </Button>
                <Popconfirm
                  title="Eliminar pack"
                  description="El pack quedará inactivo y dejará de estar disponible. Puedes reactivarlo editando su estado."
                  okText="Eliminar"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancelar"
                  onConfirm={() => onDelete(pack)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`Eliminar ${pack.name}`}
                  >
                    Eliminar
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        ),
      },
    ],
    [canWrite, onDelete, onEdit, onSelect],
  );

  const clearFilters = () =>
    onFiltersChange({ status: canFilterStatus ? 'ALL' : 'ACTIVE' });

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar los packs"
        description={error}
        action={
          <Button size="small" onClick={onRetry}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por nombre"
          value={filters.q ?? ''}
          onChange={(event) => onFiltersChange({ ...filters, q: event.target.value || undefined })}
          style={{ width: 300 }}
          aria-label="Buscar packs por nombre"
        />
        {canFilterStatus && (
          <Select<EnergyPackStatus | 'ALL'>
            value={filters.status ?? 'ALL'}
            onChange={(value) =>
              onFiltersChange({ ...filters, status: value === 'ALL' ? 'ALL' : value })
            }
            style={{ width: 200 }}
            aria-label="Filtrar por estado del pack"
            options={STATUS_OPTIONS}
          />
        )}
      </Space>

      <Table<EnergyPack>
        rowKey="id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={packs}
        scroll={{ x: 1240 }}
        pagination={{
          current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
          pageSize: meta.limit,
          total: meta.total,
          showSizeChanger: false,
          showTotal: (total) => `${total} packs`,
          onChange: (page) => onOffsetChange((page - 1) * meta.limit),
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={filtered ? 'Sin packs para los filtros' : 'Aún no hay packs'}
            >
              {filtered ? (
                <Button onClick={clearFilters}>Limpiar filtros</Button>
              ) : (
                canWrite && (
                  <Button type="primary" onClick={onCreate}>
                    Crear el primer pack
                  </Button>
                )
              )}
            </Empty>
          ),
        }}
      />
    </div>
  );
}
